/**
 * API retry utility with exponential backoff and error recovery
 */

class APIError extends Error {
  constructor(message, status, data) {
    super(message)
    this.name = 'APIError'
    this.status = status
    this.data = data
  }
}

/**
 * Retry an API call with exponential backoff
 * @param {Function} apiCall - The API call function to retry
 * @param {Object} options - Retry options
 * @returns {Promise} - The API response
 */
export async function retryWithBackoff(apiCall, options = {}) {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    retryOn = [408, 429, 500, 502, 503, 504, 524], // Include 524 for Cloudflare timeout
    onRetry = null
  } = options

  let lastError = null
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await apiCall()
      
      // If response is ok, return it
      if (response.ok || !retryOn.includes(response.status)) {
        return response
      }
      
      // If we shouldn't retry this status, throw
      if (!retryOn.includes(response.status)) {
        const errorData = await response.json().catch(() => ({}))
        throw new APIError(
          `API request failed with status ${response.status}`,
          response.status,
          errorData
        )
      }
      
      lastError = new APIError(
        `API request failed with status ${response.status}`,
        response.status,
        null
      )
    } catch (error) {
      lastError = error
      
      // If it's a network error or timeout, we should retry
      if (error.name !== 'APIError') {
        console.log(`Network error on attempt ${attempt + 1}: ${error.message}`)
      }
    }
    
    // If this wasn't the last attempt, wait before retrying
    if (attempt < maxRetries) {
      const delay = Math.min(
        initialDelay * Math.pow(backoffFactor, attempt),
        maxDelay
      )
      
      if (onRetry) {
        onRetry(attempt + 1, delay, lastError)
      }
      
      console.log(`Retrying API call in ${delay}ms (attempt ${attempt + 2}/${maxRetries + 1})`)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  
  // All retries failed
  throw lastError || new Error('API call failed after all retries')
}

/**
 * Create a fetch wrapper with retry logic
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {Object} retryOptions - Retry options
 * @returns {Promise<Response>} - The fetch response
 */
export async function fetchWithRetry(url, options = {}, retryOptions = {}) {
  // Ensure URL is absolute
  const absoluteUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`
  
  return retryWithBackoff(
    () => fetch(absoluteUrl, {
      ...options,
      // Add timeout to prevent hanging requests
      signal: AbortSignal.timeout(30000) // 30 second timeout
    }),
    retryOptions
  )
}

/**
 * Make an authenticated API call with retry logic
 * @param {string} url - The API endpoint
 * @param {Object} options - Fetch options
 * @param {string} authToken - The auth token
 * @param {Object} retryOptions - Retry options
 * @returns {Promise<any>} - The API response data
 */
export async function apiCallWithRetry(url, options = {}, authToken, retryOptions = {}) {
  // Ensure URL is absolute
  const absoluteUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`
  
  const response = await fetchWithRetry(
    absoluteUrl,
    {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': authToken ? `Bearer ${authToken}` : undefined,
        'Content-Type': 'application/json'
      }
    },
    retryOptions
  )
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new APIError(
      errorData.error || errorData.detail || `API request failed with status ${response.status}`,
      response.status,
      errorData
    )
  }
  
  return response.json()
}

// Export a hook for React components
export function useAPIRetry() {
  const handleAPIError = (error, fallbackMessage = 'An error occurred') => {
    if (error.name === 'APIError') {
      if (error.status === 524) {
        return 'Request timed out. Please try again.'
      }
      if (error.status === 403) {
        return 'Access denied. Please check your permissions.'
      }
      if (error.status === 401) {
        return 'Authentication required. Please log in again.'
      }
      if (error.status >= 500) {
        return 'Server error. Please try again later.'
      }
      return error.message || fallbackMessage
    }
    
    if (error.name === 'AbortError') {
      return 'Request timed out. Please try again.'
    }
    
    return error.message || fallbackMessage
  }
  
  return { handleAPIError }
}